import './Button.scss';
import { Component } from '@components/Component/Component.js';
import { createElement, debounce } from '@components/Utility/index.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { StyleModifiers } from "@components/Component/Button/ButtonTypes.js";
/**
 * `Button` component intended for placing buttons on forms.
 *
 * @example
 * // create a button instance
 * const button = s360.DIContainer.new('Button', '<id>', rootHtmlElement);
 * // attach a click handler
 * button.on('click', () => {console.log('clicked');})
 * // render the button inside the rootHtmlElement you provided.
 * button.draw();
 *
 * OR
 *
 * component_somebutton(button) {
 *     // setup `button` as above.
 * }
 */
export class Button extends Component {
    get inputWidth() { return this._inputWidth; }
    set inputWidth(value) {
        this._propertySet('inputWidth', value);
        this.renderInputWidthSize();
    }
    get buttonSize() { return this._buttonSize; }
    set buttonSize(value) {
        this._propertySet('buttonSize', value);
        this.renderButtonSize();
    }
    get buttonWidth() { return this._buttonWidth; }
    set buttonWidth(value) {
        this._propertySet('buttonWidth', value);
        this.renderButtonWidth();
    }
    get caption() { return this._caption || ''; }
    set caption(value) {
        this._propertySet('caption', value);
        this.renderCaption();
    }
    get tooltip() { return this._tooltip || ''; }
    set tooltip(value) {
        if ((value || '') != (this._tooltip || '')) {
            this._propertySet('tooltip', value);
            this.renderTooltip();
        }
    }
    get styleModifier() { return this._styleModifier; }
    set styleModifier(val) {
        this._propertySet('styleModifier', val);
        this.setStyleModifier();
    }
    // there should be specific properties for specific classes, ie. button-style vs container-style and primary/secondary/tertiary if needed. Not just a random classes property
    // private _classList?: string[]
    // get classList(): string[] { return this._classList || []}
    // set classList(value: string[]) {
    // 	if (value.length) {
    // 		this._propertySet('classList', value)
    // 	}
    // 	this._classList = value;
    //
    // 	this.renderClasses()
    // }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Button';
        /** Boolean. Must be set before draw. Pads the left side equal to the width of labels so that button will align with a column of inputs.  */
        this._inputWidth = false;
        this._buttonSize = '';
        this._buttonWidth = '';
        this.dynamicCodeEvents.click = 'click';
    }
    captionTypesSet() {
        this.captionTypes = {
            caption: 'caption',
            tooltip: 'tooltip'
        };
    }
    draw() {
        super.draw();
        // debounce to prevent double clicks running action twice, and to allow inputs to defocus and apply their value to the linked record.
        this.registerEvent('click', this.buttonElement, this.handleClick.bind(this));
        this.debouncedClick = debounce(this.debouncedClick, 50);
        if (this.buttonSize) {
            this.renderButtonSize();
        }
        if (this.buttonWidth) {
            this.renderButtonWidth();
        }
        if (this._inputWidth) {
            this.renderInputWidthSize();
        }
        this.setStyleModifier();
        // if (this.classList.length) {
        // 	this.renderClasses()
        // }
    }
    renderInputWidthSize() {
        if (this.drawn) {
            if (this._inputWidth) {
                this.rootElement.classList.add('inputWidth');
            }
            else {
                this.rootElement.classList.remove('inputWidth');
            }
        }
    }
    renderButtonSize() {
        if (this.drawn) {
            // Create a regular expression to match classes that exigence it with 'button-size-'
            const classRegex = /button-size-\S+/;
            // Remove all classes that match the regex from this.rootElement
            this.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.rootElement.classList.remove(className);
                }
            });
            if (this._buttonSize) {
                this.rootElement.classList.add(`button-size-${this._buttonSize}`);
            }
        }
    }
    renderButtonWidth() {
        if (this.drawn) {
            // Create a regular expression to match classes that exigence it with 'button-width-'
            const classRegex = /button-width-\S+/;
            // Remove all classes that match the regex from this.rootElement
            this.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.rootElement.classList.remove(className);
                }
            });
            if (this._buttonWidth) {
                this.rootElement.classList.add(`button-width-${this._buttonWidth}`);
            }
        }
    }
    renderCaption() {
        if (this.buttonElement) {
            this.buttonElement.textContent = this.caption;
        }
    }
    renderTooltip() {
        if (this.buttonElement) {
            this.registerTooltip(this.buttonElement, {
                tooltip: this.tooltip
            });
        }
    }
    // private renderClasses() {
    // 	if (this.buttonElement) {
    // 		this.classList.forEach((className) => this.buttonElement?.classList.add(className))
    // 	}
    // }
    /**
     * Utility function to programmatically click the button.
     */
    click(e) {
        this.trigger('click', {
            event: e
        }, e);
    }
    debouncedClick(e) {
        console.log('Button debouncedClick');
        this.click(e);
    }
    /**
     * Click event handler
     * @param e - The mouse event
     */
    handleClick(e) {
        // need to force a blur on the focused component because of timing issues encountered where sometimes clicks run before blurs (even though the act of clicking should blur the input first)
        // and we need the blur to run first to ensure that if someone was in the middle of typing a value in an input, that the value is set in the linkedRecord before running our click. 
        DIContainer.getInstance('Application').blurFocusedComponent();
        e.preventDefault();
        this.debouncedClick(e);
    }
    renderDisabledState(isDisabled) {
        super.renderDisabledState(isDisabled);
        if (this.buttonElement) {
            this.buttonElement.disabled = isDisabled;
        }
    }
    setStyleModifier() {
        if (this.buttonElement) {
            StyleModifiers.forEach((style) => {
                this.buttonElement.classList.toggle(style, style === this.styleModifier);
            });
        }
    }
    template() {
        this.buttonElement = createElement('button', ['button-style']);
        if (this._caption) {
            this.renderCaption();
        }
        if (this._tooltip) {
            this.renderTooltip();
        }
        this.rootElement.appendChild(this.buttonElement);
        return '';
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.inputWidth = ({
            id: 'inputWidth',
            value: this.inputWidth,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false,
                labelWidth: 'auto'
            },
            captionKey: 'formbuilder_button_inputwidth',
            propertyGroup: '',
            saveFlag: () => this.inputWidth
        });
        properties.buttonSize = ({
            id: 'buttonSize',
            value: this.buttonSize,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Normal' },
                    { value: 'large', display: 'Large' }
                ]
            },
            captionKey: 'formbuilder_button_buttonsize',
            propertyGroup: '',
            saveFlag: () => !!this.buttonSize
        });
        return properties;
    }
}
Button.VERSION = '$Revision: 129197 $';
